// Public inquiry form. Posts to /api/public/inquiries, which routes to
// leon.ai@dreamlink.global (Orbis) + the dashboard. Cinematic-soft brand to
// match the unified home: Archivo + DM Sans, maroon/cream, Pegasus. Service-
// aware (clipping vs localization). Turnstile-gated. No editorial register.

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiBase } from '../../lib/api';
import { Turnstile } from '../../components/Turnstile';
import { Pegasus } from '../../components/DreamlinkMark';

const API_BASE = apiBase();

const BUDGET_OPTIONS = [
  { value: 'under_5k', label: 'Under $5,000' },
  { value: '5k_to_25k', label: '$5,000 to $25,000' },
  { value: '25k_to_100k', label: '$25,000 to $100,000' },
  { value: '100k_plus', label: '$100,000+' },
  { value: 'lets_discuss', label: "Let's discuss" },
];

type SendState = 'idle' | 'sending' | 'sent' | 'captcha_failed' | 'error';

const CSS = `
.iq{--cream:#F4F0E9;--paper:#FBF8F2;--ink:#2a201e;--brown:#6F5C53;--maroon:#7a1018;--maroon-br:#9b1922;--maroon-deep:#4a0c11;--warm:#26171a;--line:rgba(42,32,30,.16);
  --disp:'Archivo',-apple-system,system-ui,sans-serif;--sans:'DM Sans',-apple-system,system-ui,sans-serif;
  min-height:100vh;background:var(--cream);color:var(--ink);font-family:var(--sans);font-weight:300;-webkit-font-smoothing:antialiased}
.iq ::selection{background:var(--maroon);color:var(--cream)}
.iq a{color:inherit;text-decoration:none}
.iqnav{display:flex;align-items:center;justify-content:space-between;padding:20px 40px}
.iqnav .mark{display:inline-flex;align-items:center;gap:9px;font-family:var(--disp);font-weight:700;font-size:16px;color:var(--maroon)}
.iqnav .mark i{font-weight:400;opacity:.7;font-style:normal;color:var(--ink)}
.iqnav .back{font-size:13px;color:var(--brown)}
.iqgrid{display:grid;grid-template-columns:0.85fr 1.15fr;min-height:calc(100vh - 70px)}
@media(max-width:860px){.iqgrid{grid-template-columns:1fr}}
.iqaside{position:relative;overflow:hidden;color:var(--cream);padding:64px 56px;display:flex;flex-direction:column;justify-content:center;
  background:radial-gradient(80% 70% at 80% 10%,#6a121b,transparent 60%),linear-gradient(165deg,#4a0c11,#26171a)}
@media(max-width:860px){.iqaside{padding:80px 28px 48px}}
.iqaside .peg{position:absolute;right:-12%;bottom:-10%;width:min(60%,420px);color:var(--cream);opacity:.07;pointer-events:none}
.iqaside .eyebrow{font-size:11px;letter-spacing:.28em;text-transform:uppercase;color:rgba(244,240,233,.6);margin-bottom:18px}
.iqaside h1{font-family:var(--disp);font-weight:600;font-size:clamp(30px,3.4vw,46px);line-height:1.05;letter-spacing:-.02em;max-width:14ch}
.iqaside p{margin-top:18px;font-size:16px;line-height:1.65;color:rgba(244,240,233,.78);max-width:38ch}
.iqaside ul{list-style:none;margin:34px 0 0;padding:0;display:flex;flex-direction:column;gap:14px}
.iqaside li{display:flex;gap:12px;align-items:center;font-size:14px;color:rgba(244,240,233,.85)}
.iqaside li::before{content:'';width:7px;height:7px;border-radius:50%;background:#f0c9b0;flex:none}
.iqmain{display:flex;align-items:center;justify-content:center;padding:56px 40px}
@media(max-width:860px){.iqmain{padding:40px 24px 64px}}
.iqcard{width:100%;max-width:460px}
.iqcard h2{font-family:var(--disp);font-weight:600;font-size:24px;letter-spacing:-.01em;margin-bottom:6px}
.iqcard .hint{font-size:14px;color:var(--brown);margin-bottom:26px}
.iqform{display:flex;flex-direction:column;gap:16px}
.iq label.l{display:block;font-family:var(--disp);font-size:12px;font-weight:600;letter-spacing:.02em;color:var(--ink);margin-bottom:6px}
.iq label.l span{color:var(--maroon);margin-left:3px}
.iq input,.iq textarea,.iq select{width:100%;padding:12px 14px;background:#fff;color:var(--ink);border:1px solid var(--line);border-radius:9px;font-size:15px;font-family:var(--sans);outline:none;transition:border-color .2s,box-shadow .2s}
.iq input:focus,.iq textarea:focus,.iq select:focus{border-color:var(--maroon);box-shadow:0 0 0 3px rgba(122,16,24,.10)}
.iq textarea{resize:vertical;line-height:1.5}
.iq select{appearance:none;cursor:pointer;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237a1018' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center}
.iqsubmit{margin-top:6px;padding:15px 28px;background:var(--maroon);color:var(--cream);border:none;border-radius:100px;font-family:var(--disp);font-weight:600;font-size:14px;letter-spacing:.02em;cursor:pointer;transition:transform .3s,background .3s}
.iqsubmit:hover{transform:translateY(-2px);background:var(--maroon-deep)}
.iqsubmit:disabled{opacity:.6;cursor:wait;transform:none}
.iqerr{color:var(--maroon-br);font-size:13px;margin:0}
.iqfoot{font-size:13px;color:var(--brown);text-align:center;margin-top:22px}
.iqfoot a{color:var(--maroon);font-weight:500}
.iqsent{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;
  background:radial-gradient(70% 60% at 50% 0%,#6a121b,transparent 60%),linear-gradient(165deg,#4a0c11,#26171a);color:var(--cream)}
.iqsent .card{max-width:480px}
.iqsent .chk{width:58px;height:58px;border-radius:50%;background:#f0c9b0;color:var(--maroon-deep);display:flex;align-items:center;justify-content:center;font-size:28px;margin:0 auto 24px;font-family:var(--disp)}
.iqsent h1{font-family:var(--disp);font-weight:600;font-size:34px;letter-spacing:-.02em;margin-bottom:12px}
.iqsent p{font-size:16px;line-height:1.6;color:rgba(244,240,233,.78)}
`;

export function Inquire() {
  const [company, setCompany] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [intent, setIntent] = useState('');
  const [budget, setBudget] = useState('');
  const [state, setState] = useState<SendState>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const isLoc =
    typeof location !== 'undefined' &&
    new URLSearchParams(location.search).get('service') === 'localization';
  const service = isLoc ? 'localization' : 'clipping';

  const onVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const onExpire = useCallback(() => setTurnstileToken(null), []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState('sending');
    setErrMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company,
          contact_name: contactName,
          contact_email: contactEmail,
          website_or_social: website,
          what_they_want: intent,
          budget_range: budget,
          service,
          turnstileToken,
          utm_source: new URLSearchParams(location.search).get('utm_source') || undefined,
          utm_campaign: new URLSearchParams(location.search).get('utm_campaign') || undefined,
        }),
      });
      if (r.status === 403) { setState('captcha_failed'); return; }
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        setErrMsg(prettyError(body.error));
        setState('error');
        return;
      }
      setState('sent');
    } catch {
      setState('error');
      setErrMsg('Something went wrong. Please try again.');
    }
  }

  if (state === 'sent') {
    return (
      <div className="iq">
        <style>{CSS}</style>
        <main className="iqsent">
          <div className="card">
            <div className="chk">✓</div>
            <h1>Got it. Talk soon.</h1>
            <p>Your inquiry is in. We review every one and reach out within one business day with next steps{isLoc ? ' and the markets we would start with' : ''}.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="iq">
      <style>{CSS}</style>
      <nav className="iqnav">
        <Link to="/" className="mark"><Pegasus size={20} color="currentColor" /> Dreamlink <i>Studio</i></Link>
        <Link to="/" className="back">← Back</Link>
      </nav>

      <div className="iqgrid">
        <aside className="iqaside">
          <span className="peg"><Pegasus size={420} color="currentColor" /></span>
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div className="eyebrow">Dreamlink Studio · {isLoc ? 'Localization' : 'Clipping'}</div>
            <h1>{isLoc ? 'Tell us about your library.' : 'Brief us on your campaign.'}</h1>
            <p>
              {isLoc
                ? 'What you have produced, and where you want it to travel. If it is a fit, we map the markets and run the localization with you.'
                : 'What you are working on and what success looks like. If it is a fit, we set you up and run your first campaign with you.'}
            </p>
            <ul>
              <li>You stay in approval</li>
              <li>We run the operation end to end</li>
              <li>A reply within one business day</li>
            </ul>
          </div>
        </aside>

        <main className="iqmain">
          <div className="iqcard">
            <h2>Start a conversation</h2>
            <div className="hint">A few details so we can come back with something useful.</div>
            <form className="iqform" onSubmit={submit}>
              <Field label="Company" required value={company} onChange={setCompany} placeholder="Acme Records" autoComplete="organization" />
              <Field label="Your name" required value={contactName} onChange={setContactName} placeholder="Jamie Lee" autoComplete="name" />
              <Field label="Work email" required type="email" value={contactEmail} onChange={setContactEmail} placeholder="jamie@acme.com" autoComplete="email" />
              <Field label="Website or main social" required value={website} onChange={setWebsite} placeholder="acme.com or @acme" autoComplete="url" />
              <div>
                <label className="l">{isLoc ? 'What do you want to localize?' : 'What do you want to do?'}<span>*</span></label>
                <textarea required value={intent} onChange={(e) => setIntent(e.target.value)} rows={4}
                  placeholder={isLoc ? 'Your library or catalogue, target markets, and what success looks like.' : 'Artist, release, content, or campaign goal. What does success look like?'} />
              </div>
              <div>
                <label className="l">Budget range<span>*</span></label>
                <select required value={budget} onChange={(e) => setBudget(e.target.value)}>
                  <option value="" disabled>Choose a range…</option>
                  {BUDGET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div style={{ marginTop: 4 }}><Turnstile onVerify={onVerify} onExpire={onExpire} theme="light" /></div>

              <button type="submit" className="iqsubmit" disabled={state === 'sending'}>
                {state === 'sending' ? 'Sending…' : 'Send inquiry'}
              </button>

              {state === 'captcha_failed' && <p className="iqerr">Please complete the verification and try again.</p>}
              {state === 'error' && <p className="iqerr">{errMsg ?? 'Something went wrong. Please try again.'}</p>}
            </form>
            <p className="iqfoot">Already a client? <Link to="/login">Sign in</Link></p>
          </div>
        </main>
      </div>
    </div>
  );
}

function prettyError(code: string | undefined): string {
  switch (code) {
    case 'company_name_required': return 'Company name is required.';
    case 'contact_name_required': return 'Your name is required.';
    case 'invalid_email': return "That email doesn't look right.";
    case 'website_or_social_required': return 'A website or social handle is required.';
    case 'what_they_want_required': return 'Tell us a bit about what you want to do.';
    case 'invalid_budget_range': return 'Pick a budget range.';
    case 'captcha_failed': return 'Verification failed. Please try again.';
    default: return 'Something went wrong. Please try again.';
  }
}

function Field({
  label, required, value, onChange, placeholder, type = 'text', autoComplete,
}: {
  label: string; required?: boolean; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; autoComplete?: string;
}) {
  return (
    <div>
      <label className="l">{label}{required && <span>*</span>}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete={autoComplete} />
    </div>
  );
}
